/**
 * WHEN IS A NEW FACE "ALREADY SOMEBODY ELSE'S"? — the one rule, for every door.
 *
 * Used by the server's device enrolment (`fieldEnroll.dedupLock.ts`), the HR web
 * enrolment (`field.enroll.ts`), the gallery audit script
 * (`scripts/find-duplicate-faces.ts`) and — MIRRORED, because the field app takes
 * no dependency on this package — by the device's own pre-check in
 * `apps/field/src/lib/enrollDedup.ts`. `enrollDedup.test.ts` in the field app
 * imports both and holds them to identical answers, at the default floors and at
 * company-set ones.
 *
 * ## The defect this replaces
 *
 * The old rule blocked when ANY single probe-template pair reached 0.60 — the
 * RECOGNITION floor. An enrolment brings 5 probes; a worker holds ~5 templates;
 * a company holds ~1000 workers. That is ~25,000 independent chances per
 * enrolment for one bad frame (motion blur, a hand across the face, a dark
 * corner) to land near somebody, and the owner met the result: two people who
 * look nothing alike, blocked as "already enrolled with a different ID".
 *
 * ## The rule
 *
 * For each OTHER worker, every probe takes its BEST cosine against that worker's
 * templates. That worker is then:
 *
 *  - BLOCK when at least ceil(60% of probes) have a best at or above the block
 *    floor AND the median per-probe best is at or above it too. A real duplicate
 *    matches from most angles, not from one.
 *  - WARN when not blocked and the same quorum reaches the warn floor. Enrolment
 *    goes ahead and HR is asked to look.
 *  - CLEAR otherwise.
 *
 * A SINGLE probe (a web photo seed is one template) has no quorum to lean on, so
 * its floors are raised instead.
 *
 * ## The floors are a company setting
 *
 * `faceRecognition.dedupWarnFloor` / `dedupBlockFloor` (settings schema). The
 * constants below are their DEFAULTS, so an install that never touches them
 * behaves exactly as before. `resolveDedupFloors` turns a stored settings object
 * into the pair every reader passes in; the single-probe floors are then
 * DERIVED, never set: single warn = max(0.65, warn), single block =
 * max(0.80, block). A single probe must never be judged more loosely than five.
 *
 * Pure: no I/O, no dependencies. `similarity` is injectable so the server can
 * pass its own full cosine and tests can pass anything.
 */

/** Per-probe best a probe needs to count toward a BLOCK (multi-probe). */
export const DEDUP_BLOCK_FLOOR = 0.75;
/** Per-probe best a probe needs to count toward a WARN (multi-probe). */
export const DEDUP_WARN_FLOOR = 0.6;
/** A single-probe enrolment blocks only at or above this. */
export const DEDUP_SINGLE_PROBE_BLOCK_FLOOR = 0.8;
/** A single-probe enrolment warns at or above this. */
export const DEDUP_SINGLE_PROBE_WARN_FLOOR = 0.65;
/** Share of probes that must reach a floor. 3 of 5. */
export const DEDUP_PROBE_QUORUM = 0.6;
/** The range an admin may set either multi-probe floor to (settings schema). */
export const DEDUP_FLOOR_MIN = 0.5;
export const DEDUP_FLOOR_MAX = 0.95;

/** The two company-set multi-probe floors. */
export interface DedupFloorSettings {
  warnFloor: number;
  blockFloor: number;
}

/**
 * The floors a stored `faceRecognition` object asks for, made safe to use.
 *
 * Anything missing, non-numeric or outside [DEDUP_FLOOR_MIN, DEDUP_FLOOR_MAX]
 * falls back to its default — a settings blob written before these keys existed
 * reads as today's behaviour. A warn floor at or above the block floor (the
 * write path refuses it, but a hand-edited blob could hold it) is clamped DOWN
 * to the block floor: block is checked first, so warn == block simply means
 * "no warn band", never a looser block.
 */
export function resolveDedupFloors(
  raw?: { dedupWarnFloor?: unknown; dedupBlockFloor?: unknown } | null
): DedupFloorSettings {
  const pick = (v: unknown, d: number): number =>
    typeof v === 'number' && Number.isFinite(v) && v >= DEDUP_FLOOR_MIN && v <= DEDUP_FLOOR_MAX ? v : d;
  const blockFloor = pick(raw?.dedupBlockFloor, DEDUP_BLOCK_FLOOR);
  const warnFloor = Math.min(pick(raw?.dedupWarnFloor, DEDUP_WARN_FLOOR), blockFloor);
  return { warnFloor, blockFloor };
}

export type DedupVerdict = 'block' | 'warn' | 'clear';

export interface DedupGalleryWorker {
  workerCode: string;
  templates: number[][];
}

export interface DedupDecision {
  verdict: DedupVerdict;
  /** The strongest OTHER worker, or null when there was nobody to compare. */
  workerCode: string | null;
  /** Median of that worker's per-probe bests. */
  medianScore: number;
  /** Probes at or above the floor the verdict was decided on (warn floor when clear). */
  matchingProbes: number;
  /** Probes compared. */
  probes: number;
}

export interface DedupRuleOptions {
  /** Skip this worker's own templates — re-enrolment never blocks against self. */
  selfWorkerCode?: string;
  /** The multi-probe WARN floor (company setting; audit script `--min`). Default DEDUP_WARN_FLOOR. */
  warnFloor?: number;
  /** The multi-probe BLOCK floor (company setting). Default DEDUP_BLOCK_FLOOR. */
  blockFloor?: number;
  similarity?: (a: number[], b: number[]) => number;
}

/** Full cosine; 0 for mismatched or zero-length/zero vectors. */
export function dedupCosine(a: number[], b: number[]): number {
  if (a.length !== b.length || a.length === 0) return 0;
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    dot += x * y;
    na += x * x;
    nb += y * y;
  }
  if (na === 0 || nb === 0) return 0;
  return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

/**
 * Floors for a given probe count. A single probe takes the HIGHER of its own
 * raised floor and the multi-probe one, so raising a company floor raises both
 * and lowering it (or `--min`) never takes a single probe below 0.65 / 0.80.
 */
export function dedupFloors(probes: number, floors: Partial<DedupFloorSettings> = {}): { block: number; warn: number } {
  const block = floors.blockFloor ?? DEDUP_BLOCK_FLOOR;
  const warn = floors.warnFloor ?? DEDUP_WARN_FLOOR;
  if (probes !== 1) return { block, warn };
  return {
    block: Math.max(DEDUP_SINGLE_PROBE_BLOCK_FLOOR, block),
    warn: Math.max(DEDUP_SINGLE_PROBE_WARN_FLOOR, warn),
  };
}

/** Probes that must reach a floor: ceil(60%), never fewer than one. */
export function dedupQuorum(probes: number): number {
  return Math.max(1, Math.ceil(DEDUP_PROBE_QUORUM * probes - 1e-9));
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? s[mid]! : (s[mid - 1]! + s[mid]!) / 2;
}

const RANK: Record<DedupVerdict, number> = { clear: 0, warn: 1, block: 2 };

/**
 * The verdict for one OTHER worker, from the incoming probes' per-probe bests.
 * Exported so the audit script can judge a pair without building a gallery.
 */
export function judgeDedupWorker(
  perProbeBest: number[],
  options: Partial<DedupFloorSettings> = {}
): Omit<DedupDecision, 'workerCode'> {
  const probes = perProbeBest.length;
  const floors = dedupFloors(probes, options);
  const need = dedupQuorum(probes);
  const med = median(perProbeBest);
  const atBlock = perProbeBest.filter((s) => s >= floors.block).length;
  const atWarn = perProbeBest.filter((s) => s >= floors.warn).length;
  if (probes > 0 && atBlock >= need && med >= floors.block) {
    return { verdict: 'block', medianScore: med, matchingProbes: atBlock, probes };
  }
  if (probes > 0 && atWarn >= need && med >= floors.warn) {
    return { verdict: 'warn', medianScore: med, matchingProbes: atWarn, probes };
  }
  return { verdict: 'clear', medianScore: med, matchingProbes: atWarn, probes };
}

/**
 * Judge `incoming` probes against every OTHER worker in `gallery` and return the
 * strongest candidate: highest verdict first, then highest median.
 */
export function decideFaceDedup(
  incoming: number[][],
  gallery: DedupGalleryWorker[],
  options: DedupRuleOptions = {}
): DedupDecision {
  const sim = options.similarity ?? dedupCosine;
  let best: DedupDecision = {
    verdict: 'clear',
    workerCode: null,
    medianScore: 0,
    matchingProbes: 0,
    probes: incoming.length,
  };
  for (const entry of gallery) {
    if (options.selfWorkerCode !== undefined && entry.workerCode === options.selfWorkerCode) continue;
    if (entry.templates.length === 0) continue;
    const perProbe = incoming.map((probe) => {
      let top = -1;
      for (const tpl of entry.templates) {
        const s = sim(probe, tpl);
        if (s > top) top = s;
      }
      return top;
    });
    const judged = judgeDedupWorker(perProbe, { warnFloor: options.warnFloor, blockFloor: options.blockFloor });
    const better =
      best.workerCode === null ||
      RANK[judged.verdict] > RANK[best.verdict] ||
      (RANK[judged.verdict] === RANK[best.verdict] && judged.medianScore > best.medianScore);
    if (better) best = { ...judged, workerCode: entry.workerCode };
  }
  return best;
}
